// src/components/rag/RAGList.tsx
import React, { useState, useEffect, RefObject, useRef } from 'react';
import { Badge, Card, Form } from 'react-bootstrap';
import { Play, Edit, Users, Plus, Power, Brain as RAGIcon, Search } from 'lucide-react';
import { Application, Group } from '../../types/knowledge-base';
import { API_ENDPOINTS } from '../../config/api.config';
import { apiService } from '../../services/apiService';
import AssignRAGGroupsModal from '../modals/common/AssignGroupsModal.tsx';
import AccessWarning from './AccessWarning';
import DescriptionDisplay from './DescriptionDisplay';
import CompactAppCard from './CompactAppCard';
import { useChatContext } from '../../contexts/ChatContext';
import '../../../scss/custom.scss';
import '../../../scss/CompactMobileStyles.scss';

// Type definitions (unchanged)
interface IndexAccess {
    indexId: string;
    hasAccess: boolean;
}

interface ApplicationAccess {
    hasRagAccess: boolean;
    indexAccess: IndexAccess[];
    ragApp: Application | null;
}

interface ApplicationWithAccess extends Application {
    accessInfo?: {
        hasRagAccess: boolean;
        accessibleIndexes: string[];
    };
}

interface ApplicationListProps {
    applications: Application[];
    onManage: (id: string) => void;
    onDeploy: (id: string) => void;
    onCreate: () => void;
    isLoading?: boolean;
    userRole?: string;
    currentPage: number;
    totalPages: number;
    total: number;
    onPageChange: (page: number) => void;
    searchQuery: string;
    onSearch: (query: string) => void;
    searchInputRef?: RefObject<HTMLInputElement>;
    className?: string;
    // These props need to be included in component parameter destructuring
    onChat?: (app: { id: string; name: string }) => void;
    isMobile?: boolean;
}

const ApplicationList: React.FC<ApplicationListProps> = ({
                                                             applications,
                                                             onManage,
                                                             onDeploy,
                                                             onCreate,
                                                             isLoading = false,
                                                             userRole = '',
                                                             currentPage,
                                                             totalPages,
                                                             total,
                                                             onPageChange,
                                                             searchQuery,
                                                             onSearch,
                                                             searchInputRef,
                                                             className,
                                                             // Include these missing props
                                                             onChat
                                                         }) => {
    // State management (unchanged except for search)
    const [appsWithAccess, setAppsWithAccess] = useState<ApplicationWithAccess[]>([]);
    const [selectedApp, setSelectedApp] = useState<Application | null>(null);
    const [showGroupsModal, setShowGroupsModal] = useState(false);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [isUpdatingGroups, setIsUpdatingGroups] = useState(false);

    // Create our own input ref if one wasn't provided
    const localInputRef = useRef<HTMLInputElement>(null);
    const actualInputRef = searchInputRef || localInputRef;

    // We'll use a debounce timer for search
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isAdmin = userRole?.toLowerCase() === 'admin';
    const { openChat: contextOpenChat } = useChatContext();

    // Set the default value when searchQuery changes externally
    useEffect(() => {
        if (actualInputRef.current && searchQuery !== actualInputRef.current.value) {
            actualInputRef.current.value = searchQuery;
        }
    }, [searchQuery]);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Rest of the useEffects remain unchanged
    useEffect(() => {
        if (selectedApp) {
            // Find the current version of the selected app
            const updatedApp = applications.find(app => app.id === selectedApp.id);
            if (updatedApp && updatedApp.enabled !== selectedApp.enabled) {
                // Update selectedApp if enabled status has changed
                setSelectedApp(updatedApp);
            }
        }
    }, [applications, selectedApp]);

    useEffect(() => {
        const checkApplicationsAccess = async () => {
            console.log("Starting to check applications access. Apps:", applications);

            const appsWithAccessPromises = applications.map(async (app) => {
                try {
                    console.log(`Checking access for app ${app.id}, name: ${app.name}`);

                    const accessInfo = await apiService.get<ApplicationAccess>(
                        `${API_ENDPOINTS.RAG.ACCESS_CHECK}/${app.id}`
                    );

                    console.log(`Access check response for ${app.name}:`, accessInfo);

                    return {
                        ...app,
                        accessInfo: {
                            hasRagAccess: accessInfo.hasRagAccess,
                            accessibleIndexes: accessInfo.indexAccess
                                .filter(index => index.hasAccess)
                                .map(index => index.indexId)
                        }
                    };
                } catch (error) {
                    console.error(`Failed to check access for application ${app.id} (${app.name}):`, error);
                    return app;
                }
            });

            const resolvedApps = await Promise.all(appsWithAccessPromises);
            console.log("Final apps with access info:", resolvedApps);
            setAppsWithAccess(resolvedApps);
        };

        if (applications.length > 0) {
            checkApplicationsAccess();
        }
    }, [applications]);


    // Debounced search handler
    const handleSearchInputChange = () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (actualInputRef.current) {
                const value = actualInputRef.current.value;
                onSearch(value);
            }
        }, 500);
    };

    // Function to handle search when enter key is pressed
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (actualInputRef.current) {
                onSearch(actualInputRef.current.value);
            }
        }
    };

    // Rest of the component methods remain unchanged
    const handleSelectApp = (app: Application) => {
        setSelectedApp(app.id === selectedApp?.id ? null : app);
    };

    // FIXED: Use the passed onChat prop if available, otherwise fall back to context
    const handleOpenChat = (app: { id: string; name: string }) => {
        if (onChat) {
            onChat(app);
        } else {
            contextOpenChat(app);
        }
    };

    const handleGroupsClick = async (app: Application) => {
        setSelectedApp(app);
        setShowGroupsModal(true); // Show modal first for better UX

        try {
            // First fetch all groups
            await fetchGroups();

            // Then fetch current group assignments
            const data = await apiService.get<{ groups: Group[] }>(
                `${API_ENDPOINTS.RAG.GROUP_ACCESS}/${app.id}`
            );

            console.log('Current group assignments:', data);

            // Make sure we're handling the group IDs correctly
            const selectedIds = data.groups?.map(g => g.id).filter(Boolean) || [];
            console.log('Selected group IDs:', selectedIds);

            setSelectedGroupIds(selectedIds);
        } catch (error) {
            console.error('Failed to fetch group assignments:', error);
            setSelectedGroupIds([]);
        }
    };

    const handleGroupsConfirm = async () => {
        if (!selectedApp) return;

        setIsUpdatingGroups(true);
        try {
            await apiService.put(
                `${API_ENDPOINTS.RAG.GROUP_ACCESS}/${selectedApp.id}`,
                { group_ids: selectedGroupIds } // Match the API's expected format
            );

            setShowGroupsModal(false);
            setSelectedApp(null);
            setSelectedGroupIds([]);

            // Optionally refetch the application list to get updated data
            // if you have a refetch function available
        } catch (error) {
            console.error('Failed to update group assignments:', error);
        } finally {
            setIsUpdatingGroups(false);
        }
    };

    const handleGroupSelectionChange = (groupId: string, checked: boolean) => {
        setSelectedGroupIds(prevIds => {
            if (checked) {
                return [...prevIds, groupId];
            }
            return prevIds.filter(id => id !== groupId);
        });
    };

    const fetchGroups = async () => {
        try {
            // Build URL with query parameters for all groups
            const queryParams = new URLSearchParams({
                all: 'true',
                sort_by: 'name',
                sort_order: 'ASC'
            });

            const url = `${API_ENDPOINTS.GROUPS.LIST}?${queryParams}`;
            console.log('Fetching all groups from:', url);

            const result = await apiService.get<{ data: Group[] }>(url);
            console.log('Fetched groups:', result);

            // Ensure we're getting the groups from the correct path in the response
            const groupsData = result.data || [];
            setGroups(groupsData);
        } catch (error) {
            console.error('Failed to fetch groups:', error);
            setGroups([]);
        }
    };

    const renderAccessMessage = (app: ApplicationWithAccess) => {
        if (!app.accessInfo) return null;

        const { hasRagAccess, accessibleIndexes } = app.accessInfo;

        if (isAdmin) return null;

        if (!hasRagAccess) {
            return <AccessWarning type="noAccess" />;
        }

        if (accessibleIndexes.length === 0) {
            return <AccessWarning type="noKBAccess" />;
        }

        if (accessibleIndexes.length < (app.index_ids?.length || 0)) {
            return (
                <AccessWarning
                    type="partialAccess"
                    indexCount={accessibleIndexes.length}
                    totalIndexes={app.index_ids?.length}
                />
            );
        }

        return null;
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => onPageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="RAG applications pagination">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => onPageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => onPageChange(totalPages)}
                                >
                                    {totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (isLoading) {
        return (
            <div className="text-center p-2">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Execute search immediately
    const handleSearchButtonClick = () => {
        if (actualInputRef.current) {
            onSearch(actualInputRef.current.value);
        }
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4 search-container">
                <div className="flex-grow-1">
                    <div className="mt-3">
                        <Form.Group>
                            <div className="d-flex gap-2">
                                <div className="position-relative flex-grow-1">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search applications..."
                                        defaultValue={searchQuery}
                                        onChange={handleSearchInputChange}
                                        onKeyDown={handleSearchKeyDown}
                                        className="border search-input pr-5"
                                        style={{height: '38px'}}
                                        ref={actualInputRef}
                                    />
                                    <button
                                        className="position-absolute end-0 top-0 btn btn-link"
                                        style={{height: '38px'}}
                                        onClick={handleSearchButtonClick}
                                    >
                                        <Search size={16} />
                                    </button>
                                </div>
                                {isAdmin && (
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={onCreate}
                                        style={{height: '38px', whiteSpace: 'nowrap'}}
                                    >
                                        <Plus size={16} className="me-1"/>
                                        New Application
                                    </button>
                                )}
                            </div>
                        </Form.Group>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className={isAdmin ? "col-md-8" : "col-md-12"}>
                    {isAdmin ? (
                        <>
                            <div className="list-group">
                                {appsWithAccess.map((app) => (
                                    <div
                                        key={app.id}
                                        className={`list-group-item py-3 px-3 application-item ${
                                            selectedApp?.id === app.id ? 'selected' : ''
                                        } ${className || ''}`}
                                        onClick={() => handleSelectApp(app)}
                                    >
                                        <div className="d-flex align-items-start justify-content-between">
                                            <div className="d-flex align-items-start flex-grow-1">
                                                <RAGIcon size={18} className="text-muted me-2 mt-1"/>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <h6 className="mb-0 text-truncate">{app.name}</h6>
                                                        {/* Status badge restored for admin view */}
                                                        <Badge
                                                            className={`ms-2 px-2 py-1 text-xs ${
                                                                app.enabled ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'
                                                            }`}
                                                        >
                                                            {app.enabled ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                    </div>
                                                    <div className="d-flex align-items-end">
                                                        <div className="flex-grow-1">
                                                            {app.description && (
                                                                <p className="text-muted small mb-1"
                                                                   style={{whiteSpace: 'pre-line'}}>
                                                                    {app.description}
                                                                </p>
                                                            )}
                                                            {renderAccessMessage(app)}
                                                        </div>
                                                        <div className="text-muted small ms-3" style={{
                                                            minWidth: '150px',
                                                            textAlign: 'right'
                                                        }}>
                                                            {app.created_at === app.updated_at ? (
                                                                <>Created on {new Date(app.created_at).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}</>
                                                            ) : (
                                                                <>Updated on {new Date(app.updated_at).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}</>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-5">
                                <div className="text-muted small">
                                    Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, total)} of {total} entries
                                </div>
                                {!isLoading && totalPages > 0 && renderPagination()}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* DESKTOP VIEW: Simple horizontal grid - NO STATUS BADGE */}
                            <div className="desktop-view">
                                <div className="row">
                                    {appsWithAccess.map((app) => (
                                        <div key={`desktop-${app.id}`} className="col-sm-6 col-lg-4 col-xl-3 mb-4">
                                            <Card className={`h-100 border shadow-sm application-item ${className || ''}`}>
                                                <Card.Body className="d-flex flex-column">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <h6 className="card-title mb-0 text-truncate flex-grow-1">{app.name}</h6>
                                                        {app.accessInfo?.hasRagAccess &&
                                                            app.accessInfo.accessibleIndexes.length > 0 && (
                                                                <button
                                                                    className="btn btn-outline-primary btn-sm ms-2"
                                                                    disabled={!app.enabled}
                                                                    onClick={() => handleOpenChat({
                                                                        id: app.id,
                                                                        name: app.name
                                                                    })}
                                                                    title={!app.enabled ? 'Application is disabled' : 'Start chat'}
                                                                >
                                                                    <Play size={14} className="me-1"/>
                                                                    Chat
                                                                </button>
                                                            )}
                                                    </div>
                                                    {app.description && (
                                                        <DescriptionDisplay
                                                            description={app.description}
                                                            appName={app.name}
                                                        />
                                                    )}
                                                    <div className="mt-auto">
                                                        <div className="access-status-container">
                                                            <div className="d-flex align-items-center">
                                                                {renderAccessMessage(app)}
                                                            </div>
                                                            <div className="text-muted small text-end">
                                                                {app.created_at === app.updated_at ? (
                                                                    <>Created on {new Date(app.created_at).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'short',
                                                                        day: 'numeric'
                                                                    })}</>
                                                                ) : (
                                                                    <>Updated on {new Date(app.updated_at).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'short',
                                                                        day: 'numeric'
                                                                    })}</>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* MOBILE VIEW: Compact list - NO STATUS BADGE */}
                            <div className="mobile-view">
                                {appsWithAccess.map((app) => (
                                    <CompactAppCard
                                        key={`mobile-${app.id}`}
                                        app={app}
                                        onChat={handleOpenChat}
                                        hideStatusBadge={true} // New prop to hide status badge
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {isAdmin && (
                    <div className="col-md-4">
                        <Card className="border shadow-sm">
                            <Card.Header className="border-bottom px-3 py-2">
                                <h6 className="mb-0">Admin Actions</h6>
                            </Card.Header>
                            <Card.Body className="p-2">
                                <div className="d-grid gap-1">
                                    <button
                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                        disabled={!selectedApp || !selectedApp.enabled}
                                        onClick={() => selectedApp && handleOpenChat({
                                            id: selectedApp.id,
                                            name: selectedApp.name
                                        })}
                                    >
                                        <Play size={16}/>
                                        Chat
                                    </button>
                                    <button
                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                        disabled={!selectedApp}
                                        onClick={() => selectedApp && onManage(selectedApp.id)}
                                    >
                                        <Edit size={16}/>
                                        Edit
                                    </button>
                                    <button
                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                        disabled={!selectedApp}
                                        onClick={() => selectedApp && handleGroupsClick(selectedApp)}
                                    >
                                        <Users size={16}/>
                                        Access
                                    </button>
                                    <button
                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                        disabled={!selectedApp}
                                        onClick={() => selectedApp && onDeploy(selectedApp.id)}
                                    >
                                        <Power size={16}/>
                                        {selectedApp?.enabled ? 'Disable' : 'Enable'}
                                    </button>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                )}
            </div>

            {isAdmin && selectedApp && (
                <AssignRAGGroupsModal
                    show={showGroupsModal}
                    application={selectedApp}
                    groups={groups}
                    selectedGroupIds={selectedGroupIds}
                    isUpdatingGroups={isUpdatingGroups}
                    onHide={() => {
                        setShowGroupsModal(false);
                        setSelectedApp(null);
                        setSelectedGroupIds([]);
                    }}
                    onConfirm={handleGroupsConfirm}
                    onGroupSelectionChange={handleGroupSelectionChange}
                />
            )}
        </>
    );
};

export default ApplicationList;